import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminAnalytics from './AdminAnalytics';
import AdminConfigSettings from './AdminConfigSettings';
import AdminUserManagement from './AdminUserManagement';
import AdminBilling from './AdminBilling';
import AdminBlogManagement from './AdminBlogManagement';
import SettingsPage from './SettingsPage';
import { settingsService } from '../services/settingsService';

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [userRole, setUserRole] = useState(null);
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    settingsService.getMe()
      .then((me) => {
        setUserRole(me.role);
        setPermissions(me.admin_permissions || []);
      })
      .catch(() => {});
  }, []);

  const isAdmin = userRole === 'admin';
  const isSales = userRole === 'sales';
  const canDashboard = isAdmin || permissions.includes('dashboard');
  const canConfig = isAdmin || permissions.includes('configurations');
  const canUsers = isAdmin;
  const canBilling = isAdmin;
  const canBlog = isAdmin || isSales;

  // Determine active page from URL
  let activePage = 'dashboard';
  if (location.pathname.includes('configuration-settings')) activePage = 'config';
  else if (location.pathname.includes('/admin/users')) activePage = 'users';
  else if (location.pathname.includes('/admin/billing')) activePage = 'billing';
  else if (location.pathname.includes('/admin/blog')) activePage = 'blog';
  else if (location.pathname.includes('/admin/profile')) activePage = 'profile';

  // For non-admin users, default to profile (or blog, for sales) if they
  // can't access dashboard.
  useEffect(() => {
    if (userRole === null) return;
    if (!isAdmin && activePage === 'dashboard' && !canDashboard) {
      navigate(isSales ? '/admin/blog' : '/admin/profile', { replace: true });
    }
  }, [userRole, activePage, isAdmin, isSales, canDashboard, navigate]);

  return (
    <div className="admin-layout-root">
      <style>{ADMIN_LAYOUT_CSS}</style>
      <aside className="admin-sidebar">
        <div className="admin-sidebar-header">
          <img src={require('../adminGallery/settings.png')} alt="" className="admin-sidebar-icon-img" />
          <span className="admin-sidebar-title">Settings</span>
        </div>
        <nav className="admin-sidebar-nav">
          {canDashboard && <button
            className={`admin-sidebar-item${activePage === 'dashboard' ? ' admin-sidebar-item--active' : ''}`}
            onClick={() => navigate('/admin/dashboard')}
          >
            <img src={require('../adminGallery/dashboard.png')} alt="" className="admin-sidebar-item-icon" /> Dashboard
          </button>}
          {canConfig && <button
            className={`admin-sidebar-item${activePage === 'config' ? ' admin-sidebar-item--active' : ''}`}
            onClick={() => navigate('/admin/configuration-settings')}
          >
            <img src={require('../adminGallery/settings.png')} alt="" className="admin-sidebar-item-icon" /> Configurations
          </button>}
          {canUsers && <button
            className={`admin-sidebar-item${activePage === 'users' ? ' admin-sidebar-item--active' : ''}`}
            onClick={() => navigate('/admin/users')}
          >
            <img src={require('../adminGallery/administrator.png')} alt="" className="admin-sidebar-item-icon" /> UserManagement
          </button>}
          {canBilling && <button
            className={`admin-sidebar-item${activePage === 'billing' ? ' admin-sidebar-item--active' : ''}`}
            onClick={() => navigate('/admin/billing')}
          >
            <img src={require('../adminGallery/bill.png')} alt="" className="admin-sidebar-item-icon" /> Billing
          </button>}
          {canBlog && <button
            className={`admin-sidebar-item${activePage === 'blog' ? ' admin-sidebar-item--active' : ''}`}
            onClick={() => navigate('/admin/blog')}
          >
            <img src={require('../adminGallery/image-gallery.png')} alt="" className="admin-sidebar-item-icon" /> Blog
          </button>}
          <button
            className={`admin-sidebar-item${activePage === 'profile' ? ' admin-sidebar-item--active' : ''}`}
            onClick={() => navigate('/admin/profile')}
          >
            <img src={require('../adminGallery/administrator.png')} alt="" className="admin-sidebar-item-icon" /> Profile Settings
          </button>
        </nav>
      </aside>
      <main className="admin-main-content">
        {activePage === 'dashboard' && canDashboard && <AdminAnalytics />}
        {activePage === 'config' && canConfig && <AdminConfigSettings />}
        {activePage === 'users' && canUsers && <AdminUserManagement />}
        {activePage === 'billing' && canBilling && <AdminBilling />}
        {activePage === 'blog' && canBlog && <AdminBlogManagement />}
        {activePage === 'profile' && <SettingsPage embedded />}
      </main>
    </div>
  );
}

const ADMIN_LAYOUT_CSS = `
.admin-layout-root { display: flex; min-height: calc(100vh - 90px); width: 100%; }

.admin-sidebar {
  width: 250px; min-width: 250px;
  background: rgba(15,23,42,0.6);
  border-right: 1px solid rgba(255,255,255,0.08);
  padding: 24px 0; display: flex; flex-direction: column;
  position: fixed; top: 90px; left: 0; height: calc(100vh - 90px); overflow-y: auto; z-index: 10;
}
[data-theme="light"] .admin-sidebar { background: rgba(248,250,252,0.95); border-right-color: rgba(99,102,241,0.1); }

.admin-sidebar-header { display: flex; align-items: center; gap: 10px; padding: 0 20px 20px; border-bottom: 1px solid rgba(255,255,255,0.06); margin-bottom: 12px; }
[data-theme="light"] .admin-sidebar-header { border-bottom-color: rgba(99,102,241,0.08); }
.admin-sidebar-icon-img { width: 26px; height: 26px; object-fit: contain; filter: invert(1) brightness(2); }
.admin-sidebar-item-icon { width: 22px; height: 22px; object-fit: contain; filter: invert(1) brightness(2); }
[data-theme="light"] .admin-sidebar-icon-img { filter: none; }
[data-theme="light"] .admin-sidebar-item-icon { filter: none; }
.admin-sidebar-title { font-size: 14px; font-weight: 700; color: var(--text-primary); }

.admin-sidebar-nav { display: flex; flex-direction: column; gap: 4px; padding: 0 12px; }
.admin-sidebar-item {
  display: flex; align-items: center; gap: 8px;
  padding: 11px 14px; border: none; border-radius: 8px;
  background: transparent; color: var(--text-secondary);
  font-size: 13px; font-weight: 500; cursor: pointer;
  transition: all 0.2s ease; text-align: left; width: 100%;
}
.admin-sidebar-item:hover { background: rgba(99,102,241,0.08); color: var(--text-primary); }
.admin-sidebar-item--active { background: rgba(99,102,241,0.15); color: #a5b4fc; font-weight: 600; }
[data-theme="light"] .admin-sidebar-item--active { color: #6366f1; background: rgba(99,102,241,0.1); }
[data-theme="light"] .admin-sidebar-item:hover { background: rgba(99,102,241,0.06); }

.admin-main-content { flex: 1; padding: 0 32px; overflow-y: auto; margin-left: 250px; }

@media (max-width: 768px) {
  .admin-layout-root { flex-direction: column; }
  .admin-sidebar { width: 100%; min-width: unset; flex-direction: row; padding: 12px; border-right: none; border-bottom: 1px solid rgba(255,255,255,0.08); position: static; height: auto; }
  .admin-sidebar-header { padding: 0 12px 0 0; border-bottom: none; margin-bottom: 0; }
  .admin-sidebar-nav { flex-direction: row; padding: 0; }
  .admin-main-content { padding: 0 16px; margin-left: 0; }
}
`;
